
import { useCallback, useEffect, useRef, useState } from 'react';
import { Exercise } from '@/models/LearnTypes';

interface UseExerciseNavigationProps {
  currentExercise: Exercise | undefined;
  currentExerciseIndex: number;
  exercises: Exercise[];
  wordsToReview: string[];
  correctlyAnsweredWords: string[];
  wasManuallyFlipped: boolean;
  isLastExercise: boolean;
  
  setCurrentExerciseIndex: React.Dispatch<React.SetStateAction<number>>;
  setWordsToReview: React.Dispatch<React.SetStateAction<string[]>>;
  setCorrectlyAnsweredWords: React.Dispatch<React.SetStateAction<string[]>>;
  resetUIState: () => void;
  
  // Perfect level tracking
  onImperfect?: () => void;
}

interface UseExerciseNavigationReturn {
  handleNextExercise: (onComplete: (wordsToReview: string[]) => void) => void;
  handlePrevExercise: () => void;
}

/**
 * Storage keys used for tracking flashcard state
 */
const STORAGE_KEYS = {
  FAILED_CARDS: 'current_level_failed_cards'
};

/**
 * Custom hook for handling navigation between exercises
 */
export const useExerciseNavigation = ({
  currentExercise,
  currentExerciseIndex,
  exercises,
  wordsToReview,
  correctlyAnsweredWords,
  wasManuallyFlipped,
  isLastExercise,
  
  setCurrentExerciseIndex,
  setWordsToReview,
  setCorrectlyAnsweredWords,
  resetUIState,
  
  // Perfect level tracking
  onImperfect
}: UseExerciseNavigationProps): UseExerciseNavigationReturn => {
  // Ref to track navigation attempts to prevent infinite loops
  const navigationAttemptRef = useRef(0);
  
  /**
   * Clean up failed card state
   */
  const cleanupFailedCardState = useCallback((): void => {
    console.log(`🧹 [useExerciseNavigation] Cleaning up failed card state`);
    
    try {
      // Clean up localStorage
      localStorage.removeItem(STORAGE_KEYS.FAILED_CARDS);
      
      // Reset navigation attempt counter
      navigationAttemptRef.current = 0;
      
      // Clean up any window globals
      if (typeof window !== 'undefined') {
        (window as any).__SAVED_EXERCISES__ = null;
        (window as any).__SAVED_EXERCISE_IDS__ = null;
        (window as any).__ORIGINAL_EXERCISES__ = null;
      }
    } catch (e) {
      console.error('❌ [useExerciseNavigation] Error cleaning up failed card state', e);
    }
  }, []);
  
  /**
   * Log current navigation state for debugging
   */
  const logNavigationState = useCallback(() => {
    if (!currentExercise) return;
    
    // Count unique and duplicate exercises
    const exerciseIds = exercises.map(ex => ex.id);
    const uniqueIds = new Set(exerciseIds);
    
    console.log(`🧭 [useExerciseNavigation] Current state:`, {
      word: currentExercise.word,
      id: currentExercise.id,
      index: currentExerciseIndex,
      isLastExercise: isLastExercise,
      wordsToReview: wordsToReview.length,
      wasManuallyFlipped: wasManuallyFlipped,
      uniqueExerciseCount: uniqueIds.size
    });
  }, [
    currentExercise, 
    currentExerciseIndex, 
    exercises, 
    wordsToReview,
    isLastExercise,
    wasManuallyFlipped
  ]);
  
  /**
   * Handle transitioning to the next exercise
   * @param onComplete Callback to invoke when all exercises are complete
   */
  const handleNextExercise = useCallback((onComplete: (wordsToReview: string[]) => void) => {
    if (!currentExercise) {
      console.warn('❌ [handleNextExercise] No current exercise, cannot proceed');
      return;
    }
    
    // Increment navigation attempt counter
    navigationAttemptRef.current += 1;
    
    // Safety check to prevent infinite loops
    if (navigationAttemptRef.current > 20) {
      console.error(`⛔ [handleNextExercise] Too many navigation attempts (${navigationAttemptRef.current}). Forcing completion to prevent infinite loop.`);
      cleanupFailedCardState();
      onComplete([]);
      return;
    }
    
    console.log(`🔄 [handleNextExercise] Processing next exercise, current index: ${currentExerciseIndex}, word: ${currentExercise.word}`);
    console.log(`🔄 [handleNextExercise] Current wordsToReview: ${wordsToReview}, length: ${wordsToReview.length}`);
    
    // Process the current card based on user interaction
    if (wasManuallyFlipped) {
      // User clicked "Practice more" - log it for analytics
      if (!wordsToReview.includes(currentExercise.id)) {
        console.log(`📝 [handleNextExercise] Adding '${currentExercise.id}' to wordsToReview for analytics`);
        setWordsToReview(prev => [...prev, currentExercise.id]);
        
        // Mark level as imperfect when a word is added to wordsToReview
        if (onImperfect) {
          console.log(`🏆 [handleNextExercise] Marking level as imperfect due to challenging word: ${currentExercise.id}`);
          onImperfect();
        }
      }
    } else {
      // Card was successfully answered
      console.log(`✓ [handleNextExercise] Card '${currentExercise.id}' was successfully answered`);
      
      // Update correctly answered words list
      if (!correctlyAnsweredWords.includes(currentExercise.id)) {
        setCorrectlyAnsweredWords(prev => [...prev, currentExercise.id]);
      }
    }
    
    // Check if we've reached the last exercise
    if (isLastExercise) {
      console.log(`🎉 [handleNextExercise] Last exercise reached, completing level`);
      cleanupFailedCardState();
      onComplete(wordsToReview);
      return;
    }
    
    // Move to next exercise
    const nextIndex = currentExerciseIndex + 1;
    if (nextIndex < exercises.length) {
      console.log(`➡️ [handleNextExercise] Moving to next exercise at index ${nextIndex}`);
      setCurrentExerciseIndex(nextIndex);
      resetUIState();
      return;
    }
    
    // Should not reach here, but just in case
    console.log(`🎉 [handleNextExercise] Reached end of exercises - completing level`);
    cleanupFailedCardState();
    onComplete(wordsToReview);
  }, [
    currentExercise,
    currentExerciseIndex,
    exercises,
    wordsToReview,
    correctlyAnsweredWords,
    wasManuallyFlipped,
    isLastExercise,
    cleanupFailedCardState,
    resetUIState,
    setCurrentExerciseIndex,
    setWordsToReview,
    setCorrectlyAnsweredWords,
    onImperfect
  ]);
  
  /**
   * Handle moving to the previous exercise
   */
  const handlePrevExercise = useCallback(() => {
    if (currentExerciseIndex <= 0) {
      console.log(`🔄 [handlePrevExercise] Already at first exercise, cannot go back`);
      return;
    }
    
    console.log(`⬅️ [handlePrevExercise] Moving to previous exercise at index ${currentExerciseIndex - 1}`);
    setCurrentExerciseIndex(prev => prev - 1);
    resetUIState();
  }, [currentExerciseIndex, setCurrentExerciseIndex, resetUIState]);
  
  /**
   * Initialize state and handle lifecycle events
   */
  useEffect(() => {
    // Reset navigation attempt counter on mount
    navigationAttemptRef.current = 0;
    
    return () => {
      // On unmount
      console.log(`🔄 [useExerciseNavigation] Component unmounting`);
    };
  }, []);

  return {
    handleNextExercise,
    handlePrevExercise
  };
};
